import React, { Component, lazy } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { Container, CssBaseline, Grid } from '@material-ui/core';
import { BRAND_PROFILES } from 'constants';
import appEventBus, { API_ADD_ACTIVE_AD_GROUP_HEADER } from 'config/appEventBus';
import { ConfigurationManager } from './utils/config';
import { FeatureTogglesProvider } from './components/FeatureTogglesProvider';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';
import PrivateRoute from './components/PrivateRoute';
import TogglesPage from './pages/TogglesPage';
import GlobalNav from './components/GlobalNav';
import SelectActiveADGroup from './components/SelectActiveADGroup';

// Lazy Loading modules
const AlterationsModule = lazy(() => import('../alterations'));
const Performance = lazy(() => import('../performance'));
const Looks = lazy(() => import('../looks'));
const ClientsModule = lazy(() => import('../clients'));
const ClientelingModule = lazy(() => import('../clienteling'));
const HomeModule = lazy(() => import('../home'));
const NotificationsModule = lazy(() => import('../notifications'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const GlobalSearchPage = lazy(() => import('./pages/GlobalSearchPage'));
const ProductsModule = lazy(() => import('../products'));

export class App extends Component {
  static propTypes = {
    config: PropTypes.instanceOf(ConfigurationManager).isRequired,
    setUser: PropTypes.func.isRequired,
    isUserLogged: PropTypes.bool.isRequired,
    isSuperUser: PropTypes.bool.isRequired,
    userId: PropTypes.string.isRequired,
    userBrand: PropTypes.string.isRequired,
    userADGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
    activeADGroup: PropTypes.string,
    classes: PropTypes.objectOf(PropTypes.string),
  };

  static defaultProps = {
    classes: {},
  };

  componentDidMount() {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        const { setUser, activeADGroup } = this.props;

        setUser({ ...user.attributes, activeADGroup });

        this.props.logger.updateContext({
          associatePin: user.attributes && user.attributes['custom:employeeNumber'],
        });

        // TODO: Workaround for AWS amplify
        localStorage.removeItem('amplify-signin-with-hostedUI');
      })
      .catch((e) => {
        console.log(e?.message); /* eslint-disable-line */
      });

    // passes initial state of active AD group (actual for super user) to API services
    // More details https://jira.mynmg.com/browse/OSP-4228
    if (this.props.activeADGroup) {
      appEventBus.emit(API_ADD_ACTIVE_AD_GROUP_HEADER, this.props.activeADGroup);
    }

    // Initialize communication with Service Workers through "message" event
    // to get notification about any errors which should showed to user
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const data = JSON.parse(event.data);
        if (data.error) {
          this.props.addNotification({
            id: `sw-error-${Date.now()}`,
            message: data.message,
            direction: 'bottomCenter',
            type: 'warning',
          });
        }
      });
    }
  }

  render() {
    const {
      isUserLogged,
      userId,
      config,
      classes,
      userBrand,
      isSuperUser,
      logger,
      activeADGroup,
      userADGroups,
    } = this.props;
    const isBGAssociate = userBrand === BRAND_PROFILES.BG;

    if (!isUserLogged) {
      return null;
    }

    if (isSuperUser && !activeADGroup) {
      return <SelectActiveADGroup />;
    }

    return (
      <FeatureTogglesProvider
        config={config}
        logger={logger}
        userId={userId}
        brand={userBrand}
        userADGroup={activeADGroup || userADGroups?.[0]}
      >
        <CssBaseline />
        <Container maxWidth="sm" className={classes.container}>
          <Grid container className={classes.gridContainer} wrap="nowrap">
            <Grid item className={classes.content}>
              <Switch>
                <Route exact path="/">
                  {isBGAssociate ? <Redirect to="/clients" /> : <HomePage />}
                </Route>
                <Route path="/logout" component={HomePage} />
                {/* Lazy Loading modules */}
                <PrivateRoute path="/alterations" component={AlterationsModule} lazy />
                <PrivateRoute path="/performance" component={Performance} lazy />
                <PrivateRoute path="/looks" component={Looks} lazy />
                <PrivateRoute path="/clients" component={ClientsModule} lazy />
                <PrivateRoute path="/clienteling" component={ClientelingModule} lazy />
                <PrivateRoute path="/home" component={HomeModule} lazy />
                <PrivateRoute path="/help" component={HelpPage} lazy />
                <PrivateRoute path="/toggles" component={TogglesPage} />
                <PrivateRoute path="/search" component={GlobalSearchPage} lazy />
                <PrivateRoute path="/notifications" component={NotificationsModule} lazy />
                <PrivateRoute path="/products" component={ProductsModule} lazy />
                <Route path="*" exact component={NotFoundPage} />
              </Switch>
            </Grid>
            <Grid item>
              <GlobalNav />
            </Grid>
          </Grid>
        </Container>
      </FeatureTogglesProvider>
    );
  }
}

export default App;
